import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSearch, FiCopy, FiCreditCard, FiBox, FiTruck, FiGift, FiDollarSign, FiRefreshCw } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { sendEmail } from '../../services/emailService';
import { formatOrderReference } from '../../utils/orderReference';

// Get Supabase URL for direct function calls
const getSupabaseUrl = () => {
  return process.env.REACT_APP_SUPABASE_URL || 'https://lxprwdplyftigaemzaua.supabase.co';
};

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid #1f2937;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ToastStack = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 2000;
  max-width: min(420px, calc(100vw - 32px));
`;

const Toast = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-left: 6px solid ${(props) => {
    if (props.$type === 'success') return '#5efba9';
    if (props.$type === 'error') return '#dc2626';
    return '#1f2937';
  }};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  padding: 12px 14px;
  color: #1f2937;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const ToastIcon = styled.div`
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    if (props.$type === 'success') return 'rgba(94, 251, 169, 0.2)';
    if (props.$type === 'error') return 'rgba(220, 38, 38, 0.12)';
    return 'rgba(31, 41, 55, 0.08)';
  }};
  color: ${(props) => {
    if (props.$type === 'success') return '#065f46';
    if (props.$type === 'error') return '#991b1b';
    return '#1f2937';
  }};
  font-weight: 800;
  font-size: 14px;
`;

const ToastContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const ToastTitle = styled.div`
  font-weight: 800;
  font-size: 13px;
  margin-bottom: 2px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.35;
  word-break: break-word;
`;

const ToastClose = styled.button`
  flex: 0 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 8px;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    gap: 16px;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.active ? '#1f2937' : '#6b7280'};
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#1f2937' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: #1f2937;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PerPageSelect = styled.select`
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    
    &:hover {
      background: #9ca3af;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;
`;

const TableHeader = styled.thead`
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: inherit;
  
  &:first-child {
    padding-left: 24px;
    border-top-left-radius: 8px;
  }
  
  &:last-child {
    padding-right: 24px;
    border-top-right-radius: 8px;
  }
`;

const TableBody = styled.tbody``;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  vertical-align: middle;
  
  &:first-child {
    padding-left: 24px;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb !important;
    transform: scale(1.001);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    
    ${TableCell} {
      background: #f9fafb;
    }
  }
  
  &:nth-child(even) {
    ${TableCell} {
      background: #fafbfc;
    }
  }

  &:last-child {
    border-bottom: none;
    
    ${TableCell} {
      border-bottom: none;
    }
  }
  
  /* Add subtle left border accent on hover */
  &:hover ${TableCell}:first-child {
    border-left: 3px solid #5efba9;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #5efba9;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const ProductCell = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ProductImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f4f6;
  border: 2px solid #f3f4f6;
  transition: all 0.2s ease;
  
  ${TableRow}:hover & {
    border-color: #5efba9;
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductName = styled.div`
  font-weight: 600;
  color: #1f2937;
  transition: color 0.2s ease;
  
  ${TableRow}:hover & {
    color: #5efba9;
  }
`;

const WhatsappLink = styled.a`
  color: #25D366;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(37, 211, 102, 0.1);

  &:hover {
    opacity: 0.9;
    transform: scale(1.15);
    background: rgba(37, 211, 102, 0.2);
    box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
  }
`;

const DeliveryAddress = styled.div`
  color: #6b7280;
  font-size: 13px;
  line-height: 1.6;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #e5e7eb;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    border-left-color: #5efba9;
  }
`;

const TrackLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrackText = styled.span`
  color: #5efba9;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CopyIcon = styled(FiCopy)`
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #5efba9;
  }
`;

const StatusTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
`;

const StatusIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$completed 
    ? 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' 
    : 'linear-gradient(135deg, #5efba9 0%, #4de89e 100%)'
  };
  color: white;
  border: 3px solid ${props => props.$completed ? '#1f2937' : '#5efba9'};
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px ${props => props.$completed 
    ? 'rgba(31, 41, 55, 0.3)' 
    : 'rgba(94, 251, 169, 0.4)'
  };
  transition: all 0.3s ease;
  position: relative;
  
  ${props => props.$clickable ? `
  &:hover {
    transform: scale(1.1);
      box-shadow: 0 4px 12px ${props.completed 
      ? 'rgba(31, 41, 55, 0.4)' 
      : 'rgba(94, 251, 169, 0.6)'
    };
  }
  ` : `
    &:hover {
      transform: scale(1);
    }
  `}
  
  ${props => props.completed && `
    &::after {
      content: '✓';
      position: absolute;
      font-size: 16px;
      font-weight: bold;
    }
  `}
`;

const StatusLine = styled.div`
  height: 3px;
  width: 40px;
  background: ${props => props.$completed 
    ? 'linear-gradient(90deg, #1f2937 0%, #5efba9 100%)' 
    : 'linear-gradient(90deg, #e5e7eb 0%, #d1d5db 100%)'
  };
  border-radius: 2px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  ${props => !props.$completed && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(94, 251, 169, 0.5), transparent);
      animation: shimmer 2s infinite;
    }
    
    @keyframes shimmer {
      0% { left: -100%; }
      100% { left: 100%; }
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #9ca3af;
`;

const SyncButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => props.syncing ? '#e5e7eb' : '#f0fdf4'};
  border: 1px solid ${props => props.syncing ? '#d1d5db' : '#5efba9'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  cursor: ${props => props.syncing ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.syncing ? '#e5e7eb' : '#5efba9'};
  }

  svg {
    animation: ${props => props.syncing ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SendEmailButton = styled.button`
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendAllEmailButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1d4ed8;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #dbeafe;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Add a mapping for the 5 status steps
const statusSteps = [
  "Shipping Request",
  "Received in the store",
  "Out for Delivery",
  "Delivered",
];

// Map backend status to step index (supports both English and Arabic from WASL API)
const getStatusStepIndex = (shipmentStatusName) => {
  if (!shipmentStatusName) return 0;
  
  const status = shipmentStatusName.toLowerCase().trim();
  
  // Step 1: Shipping Request / طلب شحن
  if (
    status === 'shipping request' ||
    status === 'طلب شحن' ||
    status.includes('shipping request')
  ) return 1;
  
  // Step 2: Received in the store / تم الاستلام بالمخزن
  if (
    status === 'received in the store' ||
    status === 'تم الاستلام بالمخزن' ||
    status.includes('received') ||
    status.includes('الاستلام') ||
    status.includes('المخزن')
  ) return 2;
  
  // Step 3: Out To Deliver / جاري التوصيل
  if (
    status === 'out to deliver' ||
    status === 'جاري التوصيل' ||
    status.includes('out to deliver') ||
    status.includes('out for delivery') ||
    status.includes('جاري') ||
    status.includes('التوصيل')
  ) return 3;
  
  // Step 4: Delivered / تم التسليم
  if (
    status === 'delivered to consignee' ||
    status === 'تم التسليم' ||
    status === 'تم التسليم للمستلم' ||
    status === 'returning to shipper' ||
    status.includes('delivered') ||
    status.includes('تم التسليم')
  ) return 4;
  
  // Refund statuses
  if (status === 'refund' || status.includes('refund')) return 5;
  if (status === 'refunded' || status.includes('refunded')) return 6;
  
  return 0;
};

function AdminStatus() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [activeTab, setActiveTab] = useState('all');

  // Helper function to translate payment status
  const getPaymentStatusText = (status) => {
    if (isArabic) {
      switch (status?.toLowerCase()) {
        case 'paid':
          return 'مدفوع';
        case 'unpaid':
          return 'غير مدفوع';
        case 'refunded':
          return 'مسترد';
        default:
          return status || 'غير مدفوع';
      }
    }
    return status || 'unpaid';
  };
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ total: 0, processed: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [displayedCount, setDisplayedCount] = useState(20);
  const [modal, setModal] = useState({ open: false, brandId: null, orderId: null });
  const [refundModal, setRefundModal] = useState({ open: false, brandId: null, orderId: null });
  const [forceOmanShipment, setForceOmanShipment] = useState(false);
  const [modalCountry, setModalCountry] = useState(null);
  const [customFieldsMap, setCustomFieldsMap] = useState({}); // key_name -> {label, label_ar}
  const [addressModal, setAddressModal] = useState({ open: false, orderId: null });
  const [addressForm, setAddressForm] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    country: 'Oman',
    governorate: '',
    city: '',
    area: '',
    delivery_notes: '',
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [emailModal, setEmailModal] = useState({
    open: false,
    orderId: null,
    orderItemId: null,
    statusName: '',
    trackingUrl: null,
    customerEmail: null,
  });
  const [sendingStatusEmail, setSendingStatusEmail] = useState(false);
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [sendingBulkEmails, setSendingBulkEmails] = useState(false);

  const showToast = ({ type = 'info', title = '', message = '' }) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, title, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3200);
  };

  // Sync statuses from WASL (non-blocking)
  const syncAllStatuses = async () => {
    try {
      setSyncing(true);
      setSyncProgress({ total: 0, processed: 0 });
      
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        setSyncing(false);
        return;
      }

      // First, get count of items that will be synced
      const { count } = await supabase
        .from('order_items')
        .select('*', { count: 'exact', head: true })
        .not('shipment_id', 'is', null);
      
      if (count !== null) {
        setSyncProgress({ total: count, processed: 0 });
      }

      const supabaseUrl = getSupabaseUrl();
      const functionUrl = `${supabaseUrl}/functions/v1/check-shipments-status`;
      
      // Start the sync but don't wait for it to complete
      const responsePromise = fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession.access_token}`,
        },
        body: JSON.stringify({}), // Empty body syncs all items with shipment_id
      });

      // Set a timeout to prevent hanging (increased to 90 seconds for large batches)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sync timeout after 90 seconds')), 90000)
      );

      const response = await Promise.race([responsePromise, timeoutPromise]);
      const responseData = await response.json();
      console.log('Sync response:', responseData);

      if (!response.ok) {
        console.error('Sync error:', responseData?.error || response.statusText);
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: isArabic ? 'حدث خطأ أثناء المزامنة' : ('Error during sync: ' + (responseData?.error || response.statusText)) });
      } else {
        // Update progress with actual processed count
        if (responseData.processed !== undefined) {
          setSyncProgress({ total: responseData.processed, processed: responseData.processed });
        }
        // Refresh data after successful sync
        fetchStatus();
        const processedCount = responseData.processed || responseData.results?.length || 0;
        showToast({ type: 'success', title: isArabic ? 'تمت' : 'Done', message: isArabic 
          ? `تمت المزامنة بنجاح (${processedCount} عنصر)` 
          : `Sync completed successfully (${processedCount} items)` });
      }
    } catch (error) {
      console.error('Error syncing statuses:', error);
      if (error.message === 'Sync timeout after 90 seconds') {
        showToast({ type: 'info', title: isArabic ? 'تنبيه' : 'Notice', message: isArabic ? 'انتهت مهلة المزامنة. قد تستمر العملية في الخلفية.' : 'Sync timeout. The process may continue in the background.' });
        // Still refresh data in case some items were updated
        fetchStatus();
      } else {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: isArabic ? 'حدث خطأ أثناء المزامنة' : ('Error syncing: ' + error.message) });
      }
    } finally {
      setSyncing(false);
      // Reset progress after a delay
      setTimeout(() => setSyncProgress({ total: 0, processed: 0 }), 3000);
    }
  };

  useEffect(() => {
    if (session) {
      // Fetch status immediately without waiting for sync
      fetchStatus();
      // Note: Sync is now only triggered manually via the "Sync All" button
    }
  }, [session]);

  useEffect(() => {
    if (session && status.length > 0) {
      // Refetch when tab changes (but don't re-sync)
      fetchStatus();
    }
  }, [activeTab]);

  // Reset displayed count when tab or search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [activeTab, searchTerm]);

  const fetchStatus = async () => {
    if (!session) return;

    try {
      setLoading(true);

      // Get the user's brand_id
      let brandId = null;
      
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle();

      if (brandManager && brandManager.brand_id) {
        brandId = brandManager.brand_id;
      } else {
        // Fallback: Try to find brand by created_by (use most recent if multiple)
        const { data: userBrands, error: brandsError } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!brandsError && userBrands && userBrands.length > 0) {
          brandId = userBrands[0].id;
          if (userBrands.length > 1) {
            console.warn('⚠️ User has multiple brands. Using the most recent one.');
          }
        }
      }

      if (!brandId) {
        console.error('Error: No brand found for user');
        setStatus([]);
        return;
      }

      // Fetch orders with order items for this brand
      const { data: orderItems, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          id,
          product_id,
          variant_id,
          quantity,
          line_total,
          tracking_url,
          shipment_status_name,
          shipment_status_id,
          shipment_id,
          isRefunded,
          note,
          attributes,
          orders!inner(
            id,
            order_number,
            created_at,
            status,
            payment_status,
            shipping_snapshot,
            profile_id
          ),
          products(
            id,
            name,
            images
          ),
          variant:product_variants(
            id,
            sku,
            variant_options:product_variant_options(
              option_group:product_option_groups(
                id,
                name,
                label
              ),
              option_value:product_option_values(
                id,
                value
              )
            )
          )
        `)
        .eq('brand_id', brandId)
        .eq('orders.payment_status', 'paid');

      if (itemsError) {
        console.error('Error fetching status:', itemsError);
        setStatus([]);
        return;
      }

      const visibleOrderItems = (orderItems || []).filter((item) => {
        const status = String(item?.orders?.status || '').toLowerCase();
        return status !== 'canceled' && status !== 'cancelled';
      });

      // Get unique profile IDs to fetch emails using Postgres function
      const uniqueProfileIds = [...new Set(visibleOrderItems.map(item => item.orders.profile_id).filter(Boolean))];
      
      // Fetch customer emails using the Postgres function (batched concurrency for speed)
      const customerEmails = {};
      const EMAIL_BATCH_SIZE = 12;
      for (let i = 0; i < uniqueProfileIds.length; i += EMAIL_BATCH_SIZE) {
        const batch = uniqueProfileIds.slice(i, i + EMAIL_BATCH_SIZE);
        const results = await Promise.all(
          batch.map(async (profileId) => {
            try {
              const { data: email, error: rpcError } = await supabase.rpc('get_user_email', {
                user_id: profileId
              });
              if (!rpcError && email) {
                return { profileId, email };
              }
            } catch (_err) {
              // Ignore single-email failure and continue
            }
            return null;
          })
        );

        results.forEach((result) => {
          if (result) {
            customerEmails[result.profileId] = result.email;
          }
        });
      }
      
      // Group items by orderId (same basket/client)
      const groupedByOrder = {};
      
      visibleOrderItems.forEach(item => {
        const orderId = item.orders.id;
        const profileId = item.orders.profile_id;
        const customerEmail = customerEmails[profileId] || null;
        
        if (!groupedByOrder[orderId]) {
          groupedByOrder[orderId] = {
            orderId: orderId,
            order_number: item.orders.order_number,
            brandId: brandId,
            items: [],
            totalAmount: 0,
            totalQuantity: 0,
            // Use the first item's shipment info for the group
            status: null,
            tracking_url: null,
            shipment_id: null,
            isRefunded: false,
            paymentStatus: item.orders.payment_status || 'unpaid',
            deliveryAddress: item.orders.shipping_snapshot || {},
            createdAt: item.orders.created_at,
            customerEmail: customerEmail,
            profileId: profileId
          };
        }
        
        // Extract additional information from attributes (custom fields are stored here)
        const additionalInfo = (item.attributes && typeof item.attributes === 'object' && Object.keys(item.attributes).length > 0) ? item.attributes : null;
        
        // Add item to the group
        groupedByOrder[orderId].items.push({
          id: item.id,
          productName: item.products?.name || 'Unknown Product',
          productImage: item.products?.images?.[0] || 'https://via.placeholder.com/60',
          quantity: item.quantity,
          amount: parseFloat(item.line_total || 0),
          status: item.shipment_status_name || 'not-started',
          tracking_url: item.tracking_url,
          shipment_id: item.shipment_id,
          isRefunded: item.isRefunded || false,
          note: item.note,
          additionalInfo: additionalInfo,
          variant: item.variant || null,
        });
        
        // Update totals
        groupedByOrder[orderId].totalAmount += parseFloat(item.line_total || 0);
        groupedByOrder[orderId].totalQuantity += item.quantity;
        
        // Use the first item with shipment info for the group status
        if (item.shipment_status_name && !groupedByOrder[orderId].status) {
          groupedByOrder[orderId].status = item.shipment_status_name;
          groupedByOrder[orderId].tracking_url = item.tracking_url;
          groupedByOrder[orderId].shipment_id = item.shipment_id;
        }
        
        // If any item is refunded, mark the group as refunded
        if (item.isRefunded) {
          groupedByOrder[orderId].isRefunded = true;
        }
      });
      
      // Convert to array and sort by createdAt descending
      const transformed = Object.values(groupedByOrder);
      transformed.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // Update payment status for refunded orders
      transformed.forEach(order => {
        if (order.isRefunded) {
          order.paymentStatus = 'Refunded';
        }
      });

      // Fetch custom fields for all products in orders
      const customFieldsMap = {};
      const allProductIds = new Set();
      orderItems.forEach(item => {
        if (item.products?.id) {
          allProductIds.add(item.products.id);
        }
      });
      
      const productIds = Array.from(allProductIds);
      if (productIds.length > 0) {
        const { data: customFieldsData } = await supabase
          .from('product_custom_fields')
          .select('key_name, label, label_ar')
          .in('product_id', productIds);
        
        if (customFieldsData) {
          customFieldsData.forEach(field => {
            customFieldsMap[field.key_name] = {
              label: field.label,
              label_ar: field.label_ar
            };
          });
        }
      }
      
      setCustomFieldsMap(customFieldsMap);
      setStatus(transformed);
    } catch (error) {
      console.error('Error fetching status:', error);
      setStatus([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredStatus = status.filter(t => {
    // Filter by tab
    let matchesTab = false;
    if (activeTab === 'all') {
      matchesTab = true;
    } else if (activeTab === 'notCreated' && getStatusStepIndex(t.status) === 0) {
      matchesTab = true;
    } else if (activeTab === 'shippingRequest' && getStatusStepIndex(t.status) === 1) {
      matchesTab = true;
    } else if (activeTab === 'received' && getStatusStepIndex(t.status) === 2) {
      matchesTab = true;
    } else if (activeTab === 'out' && getStatusStepIndex(t.status) === 3) {
      matchesTab = true;
    } else if (activeTab === 'delivered' && getStatusStepIndex(t.status) === 4) {
      matchesTab = true;
    } else if (activeTab === 'refunded' && t.paymentStatus === 'Refunded') {
      matchesTab = true;
    }

    if (!matchesTab) return false;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      const searchDigits = searchTerm.replace(/\D/g, ''); // Extract only digits from search term
      
      // If search term has digits, prioritize digit matching
      if (searchDigits.length > 0) {
        const orderIdStr = t.orderId?.toString() || '';
        const orderIdDigits = orderIdStr.replace(/\D/g, ''); // Extract only digits from order ID
        const orderNumStr = t.order_number != null ? String(t.order_number) : '';
        const phoneNumber = t.deliveryAddress?.phone_number || '';
        const phoneDigits = phoneNumber.replace(/\D/g, ''); // Extract only digits from phone number
        const email = t.customerEmail || '';
        const emailDigits = email.replace(/\D/g, ''); // Extract only digits from email
        
        const matchesOrderId = orderIdDigits.includes(searchDigits);
        const matchesOrderNumber = orderNumStr.includes(searchDigits);
        const matchesPhone = phoneDigits.includes(searchDigits);
        const matchesEmail = emailDigits.includes(searchDigits);
        const matchesProduct = t.items.some(item => 
          item.productName?.toLowerCase().includes(searchLower)
        );
        
        return matchesOrderId || matchesOrderNumber || matchesPhone || matchesEmail || matchesProduct;
      } else {
        // Text-only search
        const matchesEmail = t.customerEmail?.toLowerCase().includes(searchLower);
        const matchesOrderId = t.orderId?.toString().toLowerCase().includes(searchLower);
        const matchesOrderNumber =
          t.order_number != null && String(t.order_number).toLowerCase().includes(searchLower);
        const matchesProduct = t.items.some(item => 
          item.productName?.toLowerCase().includes(searchLower)
        );
        const phoneNumber = t.deliveryAddress?.phone_number || '';
        const matchesPhone = phoneNumber.toLowerCase().includes(searchLower);
        
        return matchesEmail || matchesOrderId || matchesOrderNumber || matchesProduct || matchesPhone;
      }
    }

    return true;
  });

  // Slice to show only displayedCount items
  const displayedStatus = filteredStatus.slice(0, displayedCount);
  const hasMore = filteredStatus.length > displayedCount;

  const statusCounts = {
    all: status.length,
    notCreated: status.filter(t => getStatusStepIndex(t.status) === 0).length,
    shippingRequest: status.filter(t => getStatusStepIndex(t.status) === 1).length,
    received: status.filter(t => getStatusStepIndex(t.status) === 2).length,
    out: status.filter(t => getStatusStepIndex(t.status) === 3).length,
    delivered: status.filter(t => getStatusStepIndex(t.status) === 4).length,
    returnRequested: 0,
    refunded: status.filter(t => t.paymentStatus === 'Refunded').length,
  };

  const handleOrder = async (brandId, orderId) => {
    setModal({ open: true, brandId, orderId });
    const selected = status.find((t) => t.orderId === orderId);
    const country = selected?.deliveryAddress?.country || null;
    setModalCountry(country);
    setForceOmanShipment(country != null && String(country).toLowerCase() !== 'oman');
  };

  const openEditAddressModal = async (orderId) => {
    const selected = status.find((t) => t.orderId === orderId);
    const snap = (selected?.deliveryAddress && typeof selected.deliveryAddress === 'object') ? selected.deliveryAddress : {};

    setAddressForm({
      full_name: snap.full_name || '',
      phone_number: snap.phone_number || '',
      email: snap.email || '',
      country: snap.country || 'Oman',
      governorate: snap.governorate || '',
      city: snap.city || '',
      area: snap.area || '',
      delivery_notes: snap.delivery_notes || '',
    });
    setAddressModal({ open: true, orderId });
  };

  const closeEditAddressModal = () => {
    setAddressModal({ open: false, orderId: null });
    setSavingAddress(false);
  };

  const saveOrderAddress = async () => {
    if (!addressModal.orderId) return;
    try {
      setSavingAddress(true);

      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        return;
      }

      // Fetch current order to merge shipping_snapshot and optionally update shipping_information
      const { data: orderRow, error: orderErr } = await supabase
        .from('orders')
        .select('id, shipping_snapshot, shipping_information_id')
        .eq('id', addressModal.orderId)
        .single();

      if (orderErr || !orderRow) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: isArabic ? 'تعذر جلب الطلب' : 'Failed to load order' });
        return;
      }

      const currentSnapshot = (orderRow.shipping_snapshot && typeof orderRow.shipping_snapshot === 'object')
        ? orderRow.shipping_snapshot
        : {};

      const cleaned = {
        full_name: addressForm.full_name?.trim() || null,
        phone_number: addressForm.phone_number?.trim() || null,
        email: addressForm.email?.trim() || null,
        country: addressForm.country?.trim() || 'Oman',
        governorate: addressForm.governorate?.trim() || null,
        city: addressForm.city?.trim() || null,
        area: addressForm.area?.trim() || null,
        delivery_notes: addressForm.delivery_notes?.trim() || null,
      };

      const mergedSnapshot = { ...currentSnapshot, ...cleaned };

      const { error: updateOrderErr } = await supabase
        .from('orders')
        .update({ shipping_snapshot: mergedSnapshot })
        .eq('id', addressModal.orderId);

      if (updateOrderErr) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: (isArabic ? 'فشل حفظ العنوان: ' : 'Failed to save address: ') + updateOrderErr.message });
        return;
      }

      // If order references shipping_information_id, update it too (so edge function uses latest)
      if (orderRow.shipping_information_id) {
        const { error: shipInfoErr } = await supabase
          .from('shipping_information')
          .update({
            full_name: mergedSnapshot.full_name,
            phone_number: mergedSnapshot.phone_number,
            email: mergedSnapshot.email,
            country: mergedSnapshot.country,
            governorate: mergedSnapshot.governorate,
            city: mergedSnapshot.city,
            area: mergedSnapshot.area,
            delivery_notes: mergedSnapshot.delivery_notes,
          })
          .eq('id', orderRow.shipping_information_id);

        if (shipInfoErr) {
          // Non-blocking: order snapshot is updated, but inform admin
          console.warn('Failed updating shipping_information:', shipInfoErr);
        }
      }

      await fetchStatus();
      closeEditAddressModal();
      showToast({ type: 'success', title: isArabic ? 'تم الحفظ' : 'Saved', message: isArabic ? 'تم حفظ العنوان' : 'Address saved' });
    } catch (err) {
      showToast({ type: 'error', title: isArabic ? 'خطأ' : 'Error', message: (isArabic ? 'حدث خطأ: ' : 'Unexpected error: ') + (err?.message || String(err)) });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleRefund = async (brandId, orderId) => {
    setRefundModal({ open: true, brandId, orderId });
  };

  const confirmOrderAction = async () => {
    setLoading(true);

    const { brandId, orderId } = modal;
    
    console.log('🚀 ========== CONFIRM ORDER ACTION STARTED ==========');
    console.log('📋 Order Details:', { orderId, brandId });
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('❌ Authentication Error: No session found');
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        setLoading(false);
        return;
      }

      // Fetch order details to get customer email
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('profile_id, id, order_number, created_at, status, payment_status')
        .eq('id', orderId)
        .single();

      if (orderError || !orderData) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: orderError?.message || (isArabic ? 'الطلب غير موجود' : 'Order not found') });
        setLoading(false);
        return;
      }

      const orderDisplayRef = formatOrderReference(orderData);

      // Get customer email using RPC function
      let customerEmail = null;
      if (orderData?.profile_id) {
        try {
          const { data: email, error: rpcError } = await supabase.rpc('get_user_email', { 
            user_id: orderData.profile_id 
          });
          
          if (!rpcError && email) {
            customerEmail = email;
          }
        } catch (emailError) {
          // Silently handle email fetch errors
        }
      }

      const supabaseUrl = getSupabaseUrl();
      const functionUrl = `${supabaseUrl}/functions/v1/create-shipment`;
      
      // Call create-shipment Edge Function
      // This creates the shipment in WASL Delivery system and updates order_items
      // Email is sent separately from the frontend using the email service
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          brandId,
          orderId,
          forceOman: forceOmanShipment,
        }),
      });

      let responseData;
      try {
        const responseText = await response.text();
        responseData = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        responseData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }

      if (!response.ok) {
        const errorMessage = responseData?.error || responseData?.message || `Failed to create shipment`;
        showToast({ type: 'error', title: isArabic ? 'فشل إنشاء الشحنة' : 'Shipment failed', message: errorMessage });
        setLoading(false);
        return;
      }

      const successMessage = responseData?.message || 'Shipment created successfully!';
      
      // Send email directly from frontend using email service
      // Email server is configured via REACT_APP_EMAIL_API_ENDPOINT environment variable
      let emailResult = null;
      
      if (customerEmail) {
        try {
          // Get order details for email content
          const { data: orderItemsData } = await supabase
            .from('order_items')
            .select(`
              tracking_url,
              orders(created_at)
            `)
            .eq('order_id', orderId)
            .limit(1)
            .single();
          
          const trackingUrl = orderItemsData?.tracking_url || null;
          const orderDate = orderItemsData?.orders?.created_at 
            ? new Date(orderItemsData.orders.created_at).toLocaleDateString() 
            : new Date().toLocaleDateString();
          
          // Create email HTML
          const emailHTML = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
              <div style="background-color: #ffffff; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h1 style="color: #5efba9; margin-bottom: 20px;">Your Shipment Has Been Created! 📦</h1>
                
                <p style="color: #333; font-size: 16px; line-height: 1.6;">
                  Dear Customer,
                </p>
                
                <p style="color: #333; font-size: 16px; line-height: 1.6;">
                  Great news! Your shipment has been created and is being processed.
                </p>
                
                <div style="background-color: #f0f0f0; padding: 20px; border-radius: 5px; margin: 20px 0;">
                  <p style="margin: 10px 0;"><strong>Order Number:</strong> #${orderDisplayRef}</p>
                  <p style="margin: 10px 0;"><strong>Order Date:</strong> ${orderDate}</p>
                  ${trackingUrl ? `<p style="margin: 10px 0;"><strong>Tracking URL:</strong> <a href="${trackingUrl}" style="color: #047857;">${trackingUrl}</a></p>` : ''}
                </div>
                
                ${trackingUrl ? `
                <div style="text-align: center; margin: 30px 0;">
                  <a href="${trackingUrl}" 
                     style="display: inline-block; padding: 15px 30px; background-color: #5efba9; color: #1f2937; text-decoration: none; border-radius: 5px; font-weight: bold;">
                    Track Your Shipment
                  </a>
                </div>
                ` : ''}
                
                <div style="background-color: #ecfdf5; padding: 20px; border-left: 4px solid #5efba9; margin: 20px 0;">
                  <p style="color: #333; font-size: 14px; margin: 0;">
                    You will receive updates as your shipment progresses. Estimated delivery is 2-5 business days.
                  </p>
                </div>
                
                <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee;">
                  <p style="color: #666; font-size: 14px; line-height: 1.6;">
                    If you have any questions about your order, please contact us at info@wasl.om
                  </p>
                </div>
                
                <div style="margin-top: 30px; text-align: center;">
                  <p style="color: #999; font-size: 12px;">
                    © ${new Date().getFullYear()} WASL. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          `;
          
          emailResult = await sendEmail({
            to: customerEmail,
            subject: `Shipment Created - Order #${orderDisplayRef}`,
            html: emailHTML,
            body: `Your shipment for order #${orderDisplayRef} has been created. ${trackingUrl ? `Track it here: ${trackingUrl}` : ''}`,
            from: 'WASL'
          });
        } catch (emailError) {
          const errorMessage = emailError.message || 'Unknown error';
          const isConnectionError = errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED');
          emailResult = { 
            success: false, 
            error: isConnectionError 
              ? 'Email server is not running. Please start the email server or check REACT_APP_EMAIL_API_ENDPOINT configuration.'
              : errorMessage
          };
        }
      }
      
      // Show success message with email status
      let emailStatusMessage = '';
      if (emailResult?.success) {
          emailStatusMessage = `📧 Email sent successfully to: ${customerEmail}\n`;
      } else if (emailResult?.error) {
        emailStatusMessage = `⚠️ Email failed: ${emailResult.error}\n`;
      } else if (!customerEmail) {
        emailStatusMessage = '⚠️ Customer email not found\n';
      }
      
      showToast({
        type: 'success',
        title: isArabic ? 'تمت العملية' : 'Done',
        message: `${successMessage}${emailStatusMessage ? ` — ${emailStatusMessage.replace(/\n/g, ' ').trim()}` : ''}`.trim()
      });
      
      fetchStatus();
      setModal({ open: false, brandId: null, orderId: null });
      setLoading(false);
      
      console.log('🏁 ========== CONFIRM ORDER ACTION COMPLETED ==========');
    } catch (error) {
      console.error('❌ ========== UNEXPECTED ERROR ==========');
      console.error('Error Details:', {
        error: error.message,
        stack: error.stack,
        orderId,
        brandId,
        errorObject: error
      });
      const errorMessage = error?.message || 'An unexpected error occurred';
      showToast({ type: 'error', title: isArabic ? 'خطأ' : 'Error', message: errorMessage });
      setLoading(false);
    }
  };

  const confirmRefundAction = async () => {
    setLoading(true);
    const { brandId, orderId } = refundModal;

    await supabase.from('order_items')
      .update({ isRefunded: true })
      .eq('order_id', orderId)
      .eq('brand_id', brandId);

    fetchStatus();

    setRefundModal({ open: false, brandId: null, orderId: null });
    setLoading(false);
  };

  const openSendEmailModal = (order) => {
    const progress = getStatusStepIndex(order.status);
    if (progress !== 3 && progress !== 4) return;
    const statusName = progress === 3 ? 'Out To Deliver' : 'Delivered To Consignee';
    const firstItemId = order.items?.[0]?.id || null;
    if (!firstItemId) {
      showToast({
        type: 'error',
        title: isArabic ? 'فشل' : 'Failed',
        message: isArabic ? 'تعذر تحديد عنصر الطلب للإيميل' : 'Could not determine order item for email'
      });
      return;
    }

    setEmailModal({
      open: true,
      orderId: order.orderId,
      orderItemId: firstItemId,
      statusName,
      trackingUrl: order.tracking_url || null,
      customerEmail: order.customerEmail || null,
    });
  };

  const closeSendEmailModal = () => {
    if (sendingStatusEmail) return;
    setEmailModal({
      open: false,
      orderId: null,
      orderItemId: null,
      statusName: '',
      trackingUrl: null,
      customerEmail: null,
    });
  };

  const confirmSendStatusEmail = async () => {
    if (!emailModal.orderItemId || !emailModal.statusName) return;
    try {
      setSendingStatusEmail(true);
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        return;
      }

      const functionUrl = `${getSupabaseUrl()}/functions/v1/send-shipment-email`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderItemId: emailModal.orderItemId,
          newStatus: emailModal.statusName,
          oldStatus: null,
          trackingUrl: emailModal.trackingUrl || null,
        }),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (_e) {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
      }

      showToast({
        type: 'success',
        title: isArabic ? 'تم الإرسال' : 'Email sent',
        message: isArabic
          ? `تم إرسال إيميل الحالة للعميل${emailModal.customerEmail ? `: ${emailModal.customerEmail}` : ''}`
          : `Shipment status email sent${emailModal.customerEmail ? ` to ${emailModal.customerEmail}` : ''}`
      });
      closeSendEmailModal();
    } catch (err) {
      showToast({
        type: 'error',
        title: isArabic ? 'فشل الإرسال' : 'Send failed',
        message: err?.message || (isArabic ? 'تعذر إرسال الإيميل' : 'Could not send email')
      });
    } finally {
      setSendingStatusEmail(false);
    }
  };

  const getBulkEmailTargets = () => {
    return filteredStatus
      .map((order) => {
        const progress = getStatusStepIndex(order.status);
        if (progress !== 3 && progress !== 4) return null;
        const orderItemId = order.items?.[0]?.id;
        if (!orderItemId || !order.customerEmail) return null;
        return {
          orderId: order.orderId,
          orderItemId,
          statusName: progress === 3 ? 'Out To Deliver' : 'Delivered To Consignee',
          trackingUrl: order.tracking_url || null,
          customerEmail: order.customerEmail,
        };
      })
      .filter(Boolean);
  };

  const confirmSendBulkEmails = async () => {
    const targets = getBulkEmailTargets();
    if (targets.length === 0) {
      showToast({
        type: 'info',
        title: isArabic ? 'لا يوجد مستلمين' : 'No recipients',
        message: isArabic ? 'لا توجد طلبات مؤهلة للإرسال الجماعي' : 'No eligible orders for bulk email'
      });
      setBulkEmailModalOpen(false);
      return;
    }

    try {
      setSendingBulkEmails(true);
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        return;
      }

      const functionUrl = `${getSupabaseUrl()}/functions/v1/send-shipment-email`;
      const results = await Promise.allSettled(
        targets.map(async (target) => {
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authSession.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderItemId: target.orderItemId,
              newStatus: target.statusName,
              oldStatus: null,
              trackingUrl: target.trackingUrl,
            }),
          });
          if (!response.ok) {
            let payload = {};
            try {
              payload = await response.json();
            } catch (_e) {
              payload = {};
            }
            throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
          }
          return target;
        })
      );

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failedCount = results.length - successCount;

      showToast({
        type: failedCount === 0 ? 'success' : 'info',
        title: failedCount === 0
          ? (isArabic ? 'تم الإرسال' : 'Emails sent')
          : (isArabic ? 'اكتمل الإرسال' : 'Bulk send completed'),
        message: isArabic
          ? `نجح: ${successCount}، فشل: ${failedCount}`
          : `Success: ${successCount}, Failed: ${failedCount}`
      });
      setBulkEmailModalOpen(false);
    } catch (err) {
      showToast({
        type: 'error',
        title: isArabic ? 'فشل الإرسال الجماعي' : 'Bulk send failed',
        message: err?.message || (isArabic ? 'تعذر إرسال الإيميلات' : 'Could not send bulk emails')
      });
    } finally {
      setSendingBulkEmails(false);
    }
  };


  // Map step index to status name for manual updates
  const stepToStatusName = [
    'Shipping Request',
    'Received in the store',
    'Out To Deliver',
    'Delivered To Consignee'
  ];

  const handleManualStatusUpdate = async (orderItemId, stepIndex) => {
    const statusName = stepToStatusName[stepIndex];
    if (!statusName) return;

    try {
      // Get old status before updating
      const { data: oldItem } = await supabase
        .from('order_items')
        .select('shipment_status_name, tracking_url')
        .eq('id', orderItemId)
        .single();

      const oldStatus = oldItem?.shipment_status_name || null;

      const { error } = await supabase
        .from('order_items')
        .update({ shipment_status_name: statusName })
        .eq('id', orderItemId);

      if (error) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: error.message });
        return;
      }

      // Trigger shipment email if status changed
      if (oldStatus !== statusName) {
        try {
          const supabaseUrl = getSupabaseUrl();
          const functionUrl = `${supabaseUrl}/functions/v1/send-shipment-email`;
          
          fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx4cHJ3ZHBseWZ0aWdhZW16YXVhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE1NDEwMjUsImV4cCI6MjA3NzExNzAyNX0.-n4yL0Nsm5sgNiu8SqwyAVZQ3x2CyT_tEiYxeRJ_c8I',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderItemId: orderItemId,
              newStatus: statusName,
              oldStatus: oldStatus,
              trackingUrl: oldItem?.tracking_url || null,
            }),
          }).catch(err => {
            console.error('Failed to send shipment email (non-blocking):', err);
          });
        } catch (emailError) {
          console.error('Error triggering shipment email:', emailError);
        }
      }

      showToast({ type: 'success', title: isArabic ? 'تم التحديث' : 'Updated', message: (isArabic ? 'تم تحديث الحالة إلى: ' : 'Status updated to: ') + statusName });
      fetchStatus();
    } catch (err) {
      showToast({ type: 'error', title: isArabic ? 'خطأ' : 'Error', message: err.message });
    }
  };

  const StatusTracker = ({ progress, brandId, orderId, orderNumber, isRefunded, orderItemId }) => {
    // Only the first step (create shipment) is clickable when progress is 0
    const canClickFirst = progress === 0;
    const orderLabel = formatOrderReference({ id: orderId, order_number: orderNumber });

    return (
      <StatusTrack
        role="presentation"
        aria-label={`Order ${orderLabel} status tracker, progress ${progress}`}
      >
        {[...Array(4)].map((_, idx) => {
          // Only the first step (create shipment) is clickable
          const canClick = idx === 0 && canClickFirst;
          
          const getTitle = () => {
            if (idx === 0 && canClickFirst) {
              return isArabic ? 'انقر لإنشاء الشحنة' : 'Click to create shipment';
            }
            if (progress > idx) {
              return isArabic ? `${statusSteps[idx]} - مكتمل` : `${statusSteps[idx]} - Completed`;
            }
            return isArabic ? `${statusSteps[idx]}` : `${statusSteps[idx]}`;
          };

          return (
            <React.Fragment key={idx}>
              <StatusIcon
                $completed={progress > idx}
                $clickable={canClick}
                title={getTitle()}
                onClick={canClick ? () => handleOrder(brandId, orderId) : undefined}
                style={{ 
                  cursor: canClick ? 'pointer' : 'default',
                  opacity: 1,
                  pointerEvents: canClick ? 'auto' : 'none'
                }}
              >
                {idx === 0 && <FiCreditCard />}
                {idx === 1 && <FiBox />}
                {idx === 2 && <FiTruck />}
                {idx === 3 && <FiGift />}
              </StatusIcon>
              {idx < 3 && <StatusLine $completed={progress > idx} />}
            </React.Fragment>
          );
        })}

        <StatusLine $completed={progress > 3} />
        <StatusIcon
          $completed={isRefunded}
          $clickable={false}
          title={isArabic ? 'استرداد' : 'Refund'}
          style={{ cursor: 'default', pointerEvents: 'none' }}
        >
          <FiDollarSign />
        </StatusIcon>
      </StatusTrack>
    );
  };

  return (
    <Container>
      <ToastStack>
        {toasts.map((t) => (
          <Toast key={t.id} $type={t.type}>
            <ToastIcon $type={t.type}>
              {t.type === 'success' ? '✓' : t.type === 'error' ? '!' : 'i'}
            </ToastIcon>
            <ToastContent>
              {t.title ? <ToastTitle>{t.title}</ToastTitle> : null}
              {t.message ? <ToastMessage>{t.message}</ToastMessage> : null}
            </ToastContent>
            <ToastClose onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))} aria-label="Close">
              ×
            </ToastClose>
          </Toast>
        ))}
      </ToastStack>
      <FilterTabs>
        <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          {isArabic ? 'الكل' : 'All'} ({statusCounts.all})
        </Tab>
        <Tab active={activeTab === 'notCreated'} onClick={() => setActiveTab('notCreated')}>
          {isArabic ? 'لم يتم إنشاء الشحنة' : 'Shipment Not Created'} ({statusCounts.notCreated})
        </Tab>
        <Tab active={activeTab === 'shippingRequest'} onClick={() => setActiveTab('shippingRequest')}>
          {isArabic ? 'طلب الشحن' : 'Shipping Request'} ({statusCounts.shippingRequest})
        </Tab>
        <Tab active={activeTab === 'received'} onClick={() => setActiveTab('received')}>
          {isArabic ? 'تم الاستلام بالمخزن' : 'Received in the store'} ({statusCounts.received})
        </Tab>
        <Tab active={activeTab === 'out'} onClick={() => setActiveTab('out')}>
          {isArabic ? 'جاري التوصيل' : 'Out for Delivery'} ({statusCounts.out})
        </Tab>
        <Tab active={activeTab === 'delivered'} onClick={() => setActiveTab('delivered')}>
          {isArabic ? 'تم التسليم' : 'Delivered'} ({statusCounts.delivered})
        </Tab>
        <Tab active={activeTab === 'refunded'} onClick={() => setActiveTab('refunded')}>
          {isArabic ? 'مسترد' : 'Refunded'} ({statusCounts.refunded})
        </Tab>
      </FilterTabs>

      <ControlsRow>
        <SearchInput
          type="text"
          placeholder={isArabic ? 'بحث' : 'Search'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SendAllEmailButton
            onClick={() => setBulkEmailModalOpen(true)}
            disabled={sendingBulkEmails || getBulkEmailTargets().length === 0}
            title={isArabic ? 'إرسال إيميل للحالات المؤهلة فقط (جاري التوصيل/تم التسليم)' : 'Send emails for eligible statuses only (Out to deliver / Delivered)'}
          >
            {isArabic ? 'إرسال إيميل للجميع' : 'Send Email to All'}
          </SendAllEmailButton>
          <SyncButton 
            syncing={syncing} 
            onClick={() => {
              if (!syncing) {
                syncAllStatuses();
              }
            }}
            disabled={syncing}
          >
            <FiRefreshCw /> {syncing ? (isArabic ? 'جاري المزامنة...' : 'Syncing...') : (isArabic ? 'مزامنة الكل' : 'Sync All')}
          </SyncButton>
          {syncing && syncProgress.total > 0 && (
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              {isArabic 
                ? `جاري معالجة ${syncProgress.processed} من ${syncProgress.total}...`
                : `Processing ${syncProgress.processed} of ${syncProgress.total}...`}
            </span>
          )}
        </div>
      </ControlsRow>

      <TableWrapper>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>
                <Checkbox type="checkbox" />
              </TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'رقم الطلب' : 'Order #'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'المنتج' : 'Product'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'البريد الإلكتروني' : 'Customer Email'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'التوصيل' : 'Delivery'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'رابط التتبع' : 'Tracking URL'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'حالة الدفع' : 'Payment Status'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'التاريخ' : 'Date'}</TableHeaderCell>
              <TableHeaderCell>{isArabic ? 'المجموع' : 'Total'}</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                  {isArabic ? 'جاري التحميل...' : 'Loading...'}
                </TableCell>
              </TableRow>
            ) : filteredStatus.length === 0 ? (
              <TableRow>
                <TableCell colSpan="10" style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <EmptyState>{isArabic ? 'لم يتم العثور على حالة' : 'No status found'}</EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              displayedStatus.map((order) => {
                const progress = getStatusStepIndex(order.status);
                const address = typeof order.deliveryAddress === 'object'
                  ? order.deliveryAddress
                  : {};
                const firstItemId = order.items[0]?.id;

                return (
                  <TableRow key={order.orderId}>
                    <TableCell>
                      <Checkbox type="checkbox" />
                    </TableCell>
                    <TableCell>
                      <strong style={{ fontSize: '14px', color: '#1f2937' }}>
                        #{formatOrderReference({ id: order.orderId, order_number: order.order_number })}
                      </strong>
                    </TableCell>
                    <TableCell>
                      <ProductCell style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        {order.items.map((item, idx) => (
                          <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: idx < order.items.length - 1 ? '12px' : '0' }}>
                            <ProductImage src={item.productImage} alt={item.productName} style={{ width: '40px', height: '40px' }} />
                            <ProductInfo style={{ flex: 1 }}>
                              <ProductName style={{ fontSize: '13px' }}>
                                {item.productName} {item.quantity > 1 && <span style={{ color: '#6b7280' }}>×{item.quantity}</span>}
                              </ProductName>
                              {item.variant?.variant_options && item.variant.variant_options.length > 0 && (
                                <div style={{ marginTop: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                                    {isArabic ? 'المتغير المحدد' : 'Selected Variant'}:
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {item.variant.variant_options.map((vo, idx) => (
                                      <span key={idx} style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        background: '#dcfce7',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        color: '#059669',
                                        fontWeight: 500
                                      }}>
                                        {vo.option_group?.label || vo.option_group?.name}: {vo.option_value?.value}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {item.note && (
                                <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#4b5563', marginTop: '2px' }}>
                                  {isArabic ? 'ملاحظة:' : 'Note:'} {item.note}
                                </div>
                              )}
                              {/* Display Additional Information if available */}
                              {item.additionalInfo && Object.keys(item.additionalInfo).length > 0 && (
                                <div style={{ marginTop: '6px', fontSize: '11px', color: '#374151' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1f2937' }}>
                                    {isArabic ? 'معلومات إضافية' : 'Additional Information'}:
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    {Object.entries(item.additionalInfo).map(([key, value]) => {
                                      // Skip null, undefined, or empty values
                                      if (value === null || value === undefined || value === '') return null;
                                      
                                      // Get label from custom fields map
                                      const fieldInfo = customFieldsMap[key];
                                      const displayLabel = fieldInfo 
                                        ? (isArabic && fieldInfo.label_ar ? fieldInfo.label_ar : fieldInfo.label)
                                        : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                      
                                      // Format the value
                                      let displayValue = value;
                                      if (Array.isArray(value)) {
                                        displayValue = value.join(', ');
                                      } else if (typeof value === 'object') {
                                        displayValue = JSON.stringify(value, null, 2);
                                      } else {
                                        displayValue = String(value);
                                      }
                                      
                                      return (
                                        <div key={key} style={{ display: 'flex', gap: '6px', fontSize: '10px' }}>
                                          <span style={{ fontWeight: 500, minWidth: '80px' }}>{displayLabel}:</span>
                                          <span style={{ color: '#6b7280' }}>{displayValue}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </ProductInfo>
                          </div>
                        ))}
                        {order.items.length > 0 && (
                          <WhatsappLink
                            href={
                              address.phone_number
                                ? `https://wa.me/${address.phone_number.replace(/\D/g, '')}`
                                : "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            title={isArabic ? 'تواصل عبر واتساب' : 'Contact via WhatsApp'}
                            style={{ marginTop: '4px' }}
                          >
                            <FaWhatsapp /> {isArabic ? 'تواصل' : 'Contact'}
                          </WhatsappLink>
                        )}
                        {order.items.length > 1 && (
                          <span style={{ fontSize: '11px', color: '#059669', fontWeight: '600', background: '#d1fae5', padding: '2px 8px', borderRadius: '4px' }}>
                            {order.items.length} {isArabic ? 'عناصر • نفس التوصيل' : 'items • Same delivery'}
                          </span>
                        )}
                      </ProductCell>
                    </TableCell>
                    <TableCell>
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#5efba9',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        wordBreak: 'break-word'
                      }}>
                        {order.customerEmail ? (
                          <>
                            <span style={{ fontSize: '16px' }}>📧</span>
                            <span>{order.customerEmail}</span>
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                            {isArabic ? 'غير متوفر' : 'No email'}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DeliveryAddress>
                        {address.full_name && <div style={{ fontWeight: '600', marginBottom: '4px' }}>{address.full_name}</div>}
                        {address.phone_number && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            marginBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>📱</span> {address.phone_number}
                          </div>
                        )}
                        {address.area && address.city && address.governorate && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                            📍 {address.area}, {address.city}, {address.governorate}, Oman
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => openEditAddressModal(order.orderId)}
                          style={{
                            marginTop: 8,
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #e5e7eb',
                            background: 'white',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#1f2937',
                            alignSelf: 'flex-start'
                          }}
                        >
                          {isArabic ? 'تعديل العنوان' : 'Edit address'}
                        </button>
                      </DeliveryAddress>
                    </TableCell>
                    <TableCell>
                      <TrackLink>
                        {order.tracking_url ? (
                          <>
                            <TrackText as="a" href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                              {isArabic ? 'تتبع الطلب' : 'Track Order'}
                            </TrackText>
                            <CopyIcon onClick={() => navigator.clipboard.writeText(order.tracking_url)} title={isArabic ? 'نسخ الرابط' : 'Copy URL'} />
                          </>
                        ) : (
                          <TrackText style={{ color: '#ccc', cursor: 'not-allowed' }}>
                            {isArabic ? 'لا يوجد تتبع' : 'No Tracking'}
                          </TrackText>
                        )}
                      </TrackLink>
                    </TableCell>
                    <TableCell>
                      <StatusTracker
                        progress={progress}
                        brandId={order.brandId}
                        orderId={order.orderId}
                        orderNumber={order.order_number}
                        isRefunded={order.isRefunded}
                        orderItemId={firstItemId}
                      />
                      {(progress === 3 || progress === 4) && order.customerEmail && firstItemId && (
                        <SendEmailButton onClick={() => openSendEmailModal(order)}>
                          {isArabic ? 'إرسال إيميل للعميل' : 'Send client email'}
                        </SendEmailButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {getPaymentStatusText(order.paymentStatus)}
                    </TableCell>
                    <TableCell>
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {order.totalAmount?.toFixed(2) ?? '-'} OMR
                      {order.items.length > 1 && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          ({order.totalQuantity} items)
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {!loading && hasMore && (
        <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
          {isArabic ? 'تحميل المزيد' : 'Load More'} ({filteredStatus.length - displayedCount} {isArabic ? 'متبقي' : 'remaining'})
        </LoadMoreButton>
      )}

      {modal.open && (
        <ModalOverlay>
          <ModalContent>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              Are you sure you want to perform this action?
            </div>
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              This will confirm and process the order.
            </div>
            {modalCountry && String(modalCountry).toLowerCase() !== 'oman' && (
              <div style={{
                marginTop: 12,
                marginBottom: 6,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #fde68a',
                background: '#fffbeb',
                color: '#92400e',
                fontSize: 13
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {isArabic ? 'طلب دولي' : 'International order'}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={forceOmanShipment}
                    onChange={(e) => setForceOmanShipment(e.target.checked)}
                  />
                  <span>
                    {isArabic
                      ? 'إنشاء الشحنة كأنها داخل عمان'
                      : 'Create shipment as Oman'}
                  </span>
                </label>
                <div style={{ marginTop: 6, opacity: 0.9 }}>
                  {isArabic
                    ? `الدولة الحالية: ${modalCountry}`
                    : `Current country: ${modalCountry}`}
                </div>
              </div>
            )}
            <ModalActions>
              <ModalButton onClick={() => setModal({ open: false, brandId: null, orderId: null })}>
                Cancel
              </ModalButton>
              <ModalButton $primary onClick={confirmOrderAction}>
                Confirm
              </ModalButton>
              {loading && <Spinner />}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {refundModal.open && (
        <ModalOverlay>
          <ModalContent>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              Are you sure you want to refund the order?
            </div>
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              This will change the order status.
            </div>
            <ModalActions>
              <ModalButton onClick={() => setRefundModal({ open: false, brandId: null, orderId: null })}
                disabled={loading}>
                Cancel
              </ModalButton>
              <ModalButton $primary onClick={confirmRefundAction}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading ? 0.7 : 1
                }}>
                Confirm
              </ModalButton>
              {loading && <Spinner />}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {addressModal.open && (
        <ModalOverlay onClick={closeEditAddressModal}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ textAlign: 'left', maxWidth: 560 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              {isArabic ? 'تعديل عنوان الطلب' : 'Edit order address'}
            </div>
            <div style={{ color: '#6b7280', marginBottom: 16, fontSize: 13 }}>
              {isArabic
                ? 'سيتم تحديث عنوان الشحن المستخدم للشحنة.'
                : 'This updates the shipping address used for shipment creation.'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'الاسم' : 'Full name'}</div>
                <input
                  value={addressForm.full_name}
                  onChange={(e) => setAddressForm((p) => ({ ...p, full_name: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'رقم الهاتف' : 'Phone'}</div>
                <input
                  value={addressForm.phone_number}
                  onChange={(e) => setAddressForm((p) => ({ ...p, phone_number: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'البريد الإلكتروني' : 'Email'}</div>
                <input
                  value={addressForm.email}
                  onChange={(e) => setAddressForm((p) => ({ ...p, email: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'الدولة' : 'Country'}</div>
                <input
                  value={addressForm.country}
                  onChange={(e) => setAddressForm((p) => ({ ...p, country: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'المحافظة' : 'Governorate'}</div>
                <input
                  value={addressForm.governorate}
                  onChange={(e) => setAddressForm((p) => ({ ...p, governorate: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'المدينة' : 'City'}</div>
                <input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'المنطقة' : 'Area'}</div>
                <input
                  value={addressForm.area}
                  onChange={(e) => setAddressForm((p) => ({ ...p, area: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{isArabic ? 'ملاحظات التوصيل' : 'Delivery notes'}</div>
                <textarea
                  value={addressForm.delivery_notes}
                  onChange={(e) => setAddressForm((p) => ({ ...p, delivery_notes: e.target.value }))}
                  rows={3}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', resize: 'vertical' }}
                />
              </div>
            </div>

            <ModalActions style={{ justifyContent: 'flex-end' }}>
              <ModalButton onClick={closeEditAddressModal} disabled={savingAddress}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </ModalButton>
              <ModalButton $primary onClick={saveOrderAddress} disabled={savingAddress}>
                {savingAddress ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (isArabic ? 'حفظ' : 'Save')}
              </ModalButton>
              {savingAddress && <Spinner />}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {emailModal.open && (
        <ModalOverlay>
          <ModalContent>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              {isArabic ? 'تأكيد إرسال إيميل الحالة' : 'Confirm shipment status email'}
            </div>
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              {isArabic
                ? `سيتم إرسال إيميل للعميل بحالة الشحنة: ${emailModal.statusName}`
                : `This will send a shipment status email to the client: ${emailModal.statusName === 'Out To Deliver' ? 'Out for Delivery' : emailModal.statusName === 'Delivered To Consignee' ? 'Delivered' : emailModal.statusName}`}
            </div>
            {emailModal.customerEmail && (
              <div style={{ color: '#374151', marginBottom: 12, fontSize: 13 }}>
                {isArabic ? 'المستلم:' : 'Recipient:'} <strong>{emailModal.customerEmail}</strong>
              </div>
            )}
            <ModalActions>
              <ModalButton onClick={closeSendEmailModal} disabled={sendingStatusEmail}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </ModalButton>
              <ModalButton $primary onClick={confirmSendStatusEmail} disabled={sendingStatusEmail}>
                {sendingStatusEmail ? (isArabic ? 'جاري الإرسال...' : 'Sending...') : (isArabic ? 'تأكيد الإرسال' : 'Confirm send')}
              </ModalButton>
              {sendingStatusEmail && <Spinner />}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {bulkEmailModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              {isArabic ? 'تأكيد الإرسال الجماعي' : 'Confirm bulk email send'}
            </div>
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              {isArabic
                ? `سيتم إرسال إيميل للحالات المؤهلة فقط (جاري التوصيل / تم التسليم). العدد: ${getBulkEmailTargets().length}`
                : `This will send emails for eligible statuses only (Out for Delivery / Delivered). Count: ${getBulkEmailTargets().length}`}
            </div>
            <ModalActions>
              <ModalButton onClick={() => !sendingBulkEmails && setBulkEmailModalOpen(false)} disabled={sendingBulkEmails}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </ModalButton>
              <ModalButton $primary onClick={confirmSendBulkEmails} disabled={sendingBulkEmails}>
                {sendingBulkEmails ? (isArabic ? 'جاري الإرسال...' : 'Sending...') : (isArabic ? 'تأكيد الإرسال' : 'Confirm send')}
              </ModalButton>
              {sendingBulkEmails && <Spinner />}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default AdminStatus;

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 32px 24px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
  min-width: 320px;
  max-width: 90vw;
  text-align: center;
`;

const ModalActions = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  background: ${props => props.$primary ? '#5efba9' : '#e5e7eb'};
  color: ${props => props.$primary ? '#1f2937' : '#6b7280'};
  &:hover {
    opacity: 0.9;
  }
`;
